import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight,
  Link2, Link2Off,
  Eraser, X, Check,
  Undo2, Redo2, Quote, Minus,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import './rich-text-editor.css';

const SWATCHES = [
  '#000000', '#374151', '#6B7280', '#9CA3AF', '#D1D5DB', '#F3F4F6', '#FFFFFF',
  '#DC2626', '#EA580C', '#D97706', '#EAB308', '#22C55E', '#0EA5E9', '#6366F1',
  '#BE123C', '#9A3412', '#92400E', '#166534', '#065F46', '#1E40AF', '#5B21B6',
  '#FCA5A5', '#FED7AA', '#FEF08A', '#BBF7D0', '#BAE6FD', '#C7D2FE', '#F5D0FE',
];

function ColorPickerPopover({ currentColor, onSelect, onClear, label, indicator }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen(o => !o); }}
        title={label}
        className="inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-sm text-muted-foreground hover:bg-muted"
      >
        {indicator(currentColor)}
        <span className="text-[10px]">▾</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 p-2 bg-popover border rounded-md shadow-md min-w-max">
          <div className="grid grid-cols-7 gap-1">
            {SWATCHES.map(color => (
              <button
                key={color}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(color);
                  setOpen(false);
                }}
                title={color}
                className={cn(
                  'w-5 h-5 rounded-sm border transition-transform hover:scale-110',
                  color === '#FFFFFF' ? 'border-border' : 'border-transparent',
                  currentColor === color && 'ring-2 ring-ring ring-offset-1'
                )}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          {onClear && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onClear();
                setOpen(false);
              }}
              className="mt-2 w-full flex items-center gap-1 px-1 py-0.5 text-xs text-muted-foreground hover:text-foreground rounded hover:bg-muted"
            >
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ToolbarButton({ onClick, isActive, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      title={title}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center rounded px-1.5 py-1 text-sm transition-colors',
        disabled
          ? 'opacity-30 cursor-not-allowed'
          : 'hover:bg-muted cursor-pointer',
        isActive
          ? 'bg-muted text-foreground'
          : 'text-muted-foreground'
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-border mx-0.5 self-center" />;
}

function LinkPopover({ isActive, currentHref, selectedText, onSet, onRemove }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const ref = useRef(null);
  const urlRef = useRef(null);
  const textRef = useRef(null);

  const openPopover = () => {
    setText(selectedText || '');
    setUrl(currentHref || 'https://');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    // Focus text field if empty, otherwise URL field
    setTimeout(() => {
      if (!selectedText) {
        textRef.current?.focus();
      } else {
        urlRef.current?.select();
      }
    }, 0);
  }, [open, selectedText]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const keyHandler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open]);

  const handleSet = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;
    onSet(trimmedUrl, text.trim());
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); openPopover(); }}
        title={isActive ? 'Edit link' : 'Insert link'}
        className={cn(
          'inline-flex items-center justify-center rounded px-1.5 py-1 text-sm transition-colors hover:bg-muted',
          isActive ? 'bg-muted text-foreground' : 'text-muted-foreground'
        )}
      >
        <Link2 className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 p-3 bg-popover border rounded-md shadow-md w-72">
          <p className="text-xs font-medium text-foreground mb-3">
            {isActive ? 'Edit link' : 'Insert link'}
          </p>

          <div className="space-y-2 mb-2">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Text</label>
              <input
                ref={textRef}
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); urlRef.current?.focus(); } }}
                placeholder="Display text"
                className="w-full h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">URL</label>
              <div className="flex gap-1.5">
                <input
                  ref={urlRef}
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSet(); } }}
                  placeholder="https://"
                  className="flex-1 h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSet(); }}
                  disabled={!url.trim()}
                  className="inline-flex items-center justify-center h-7 w-7 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                  title="Set link"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setOpen(false); }}
                  className="inline-flex items-center justify-center h-7 w-7 rounded border border-border text-muted-foreground hover:bg-muted"
                  title="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>

          {isActive && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onRemove(); setOpen(false); }}
              className="mt-1 w-full flex items-center gap-1 px-1 py-0.5 text-xs text-destructive hover:text-destructive rounded hover:bg-muted"
            >
              <Link2Off className="h-3 w-3" /> Remove link
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const RichTextEditor = forwardRef(({ value, onChange, placeholder }, ref) => {
  const [activeTextColor, setActiveTextColor] = useState('#000000');
  const [activeBgColor, setActiveBgColor] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.extend({ inclusive: false }).configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder || 'Start writing...' }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'rte-content',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.isEmpty ? '' : editor.getHTML());
    },
    onSelectionUpdate: ({ editor }) => {
      const color = editor.getAttributes('textStyle').color;
      setActiveTextColor(color || '#000000');
      const bg = editor.getAttributes('highlight').color;
      setActiveBgColor(bg || null);
    },
  });

  // Sync external value changes (e.g. loading a draft)
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text) => {
      if (editor) {
        editor.chain().focus().insertContent(text).run();
      }
    },
  }));

  if (!editor) return null;

  return (
    <div className="border rounded-md overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b bg-muted/20">

        {/* Undo / Redo */}
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarButton>

        <Divider />

        {/* Heading selector */}
        <select
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' : '0'
          }
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const level = parseInt(e.target.value);
            if (level === 0) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().toggleHeading({ level }).run();
            }
          }}
          className="h-7 text-xs rounded border border-border bg-background px-1.5 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="0">Normal</option>
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
        </select>

        <Divider />

        {/* Text formatting */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough">
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarButton>

        <Divider />

        {/* Text colour */}
        <ColorPickerPopover
          currentColor={activeTextColor}
          onSelect={(color) => {
            setActiveTextColor(color);
            editor.chain().focus().setColor(color).run();
          }}
          label="Text colour"
          indicator={(color) => (
            <span className="font-bold text-sm leading-none" style={{ color }}>A</span>
          )}
        />

        {/* Background colour */}
        <ColorPickerPopover
          currentColor={activeBgColor}
          onSelect={(color) => {
            setActiveBgColor(color);
            editor.chain().focus().setHighlight({ color }).run();
          }}
          onClear={() => {
            setActiveBgColor(null);
            editor.chain().focus().unsetHighlight().run();
          }}
          label="Background colour"
          indicator={(color) => (
            <span
              className="font-bold text-sm leading-none px-0.5 rounded-sm"
              style={{ backgroundColor: color || 'transparent', outline: color ? 'none' : '1px dashed currentColor' }}
            >A</span>
          )}
        />

        <Divider />

        {/* Lists */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Ordered list">
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet list">
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Blockquote">
          <Quote className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
          <Minus className="h-3.5 w-3.5" />
        </ToolbarButton>

        <Divider />

        {/* Alignment */}
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align left">
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align centre">
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align right">
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarButton>

        <Divider />

        {/* Link */}
        <LinkPopover
          isActive={editor.isActive('link')}
          currentHref={editor.getAttributes('link').href || ''}
          selectedText={
            editor.state.selection.empty
              ? ''
              : editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' ')
          }
          onSet={(url, text) => {
            const { empty, from, to } = editor.state.selection;
            if (editor.isActive('link')) {
              editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            } else if (!empty) {
              editor.chain().focus().setLink({ href: url }).run();
            } else {
              editor.chain().focus()
                .insertContent({ type: 'text', text: text || url, marks: [{ type: 'link', attrs: { href: url } }] })
                .run();
            }
          }}
          onRemove={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
        />

        <Divider />

        {/* Clear formatting */}
        <ToolbarButton onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear formatting">
          <Eraser className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;
